/**
 * liquidSkinPipeline — a fluid metallic surface that ripples with
 * bass but flows smoothly without sharp edges. The "liquid skin"
 * is a horizontal sheet that bends and re-forms with each pulse.
 *
 * Heavy use of normal-perturbed reflection; the displacement is
 * integrated via a wave equation approximation in the shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface LiquidSkinPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(443.897, 441.423));
  p += dot(p, p + 19.19);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1, 0));
  float c = hash21(i + vec2(0, 1));
  float d = hash21(i + vec2(1, 1));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm2(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * valueNoise(p);
    p = p * 2.03 + vec2(1.7, -3.1);
    a *= 0.5;
  }
  return v;
}

// Animated fluid height field. Sum of traveling wave packets.
float waveHeight(vec2 q, float t) {
  float h = 0.0;
  // Six wave packets, each from a different direction and frequency.
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    vec2 dir = normalize(vec2(cos(fi * 1.05), sin(fi * 1.05)));
    float speed = 1.0 + 0.2 * fi;
    float k = 3.0 + 0.6 * fi;
    float amp = 0.05 + 0.03 * sin(t * 0.4 + fi);
    h += sin(dot(q, dir) * k - t * speed) * amp;
  }
  return h;
}

float mapLiquid(vec3 p) {
  // Horizontal sheet near z = 0, with falloff at the edges.
  float r = length(p.xy);
  float fall = smoothstep(1.4, 0.6, r);
  if (fall <= 0.0) return 1.0; // outside
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  bass = pow(clamp(bass * 2.4, 0.0, 1.0), 0.65);
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  mid = pow(clamp(mid * 2.7, 0.0, 1.0), 0.8);
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  high = pow(clamp(high * 3.6, 0.0, 1.0), 0.85);
  float h = waveHeight(p.xy * 1.5, uTime) * (0.5 + bass * 0.7);
  h += (fbm2(p.xy * 2.5 + vec2(uTime * 0.15, -uTime * 0.12)) - 0.5) * 0.2 * (0.4 + mid);
  h += (fbm2(p.xy * 12.0 + vec2(uTime * 0.3, 0.0)) - 0.5) * 0.04 * (0.4 + high);
  return (p.z - h) / fall;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(
    mapLiquid(p + e.xyy) - mapLiquid(p - e.xyy),
    mapLiquid(p + e.yxy) - mapLiquid(p - e.yxy),
    mapLiquid(p + e.yyx) - mapLiquid(p - e.yyx)
  ));
}

void main() {
  vec2 uv = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  uv.x *= uResolution.x / uResolution.y;
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.4, 0.0, 1.0), 0.65);
  mid = pow(clamp(mid * 2.7, 0.0, 1.0), 0.8);
  high = pow(clamp(high * 3.6, 0.0, 1.0), 0.85);

  // Camera looking down at an angle.
  vec3 ro = vec3(0.0, -1.4, 1.4 + 0.05 * sin(uTime * 0.2));
  vec3 ww = normalize(vec3(0.0, 0.0, 0.0) - ro);
  vec3 uu = normalize(cross(ww, vec3(0.0, 0.0, 1.0)));
  vec3 vv = cross(uu, ww);
  vec3 rd = normalize(uv.x * uu + uv.y * vv + 1.8 * ww);

  float t = 0.0;
  bool hit = false;
  vec3 p = ro;
  for (int i = 0; i < 110; i++) {
    float d = mapLiquid(p);
    if (d < 0.0008) { hit = true; break; }
    if (t > 8.0) break;
    t += d * 0.9;
    p = ro + rd * t;
  }
  vec3 col = vec3(0.012, 0.015, 0.028);
  if (hit) {
    vec3 n = calcNormal(p);
    vec3 ldir = normalize(vec3(0.4, 0.6, 0.8));
    vec3 h = normalize(ldir - rd);
    float diff = clamp(dot(n, ldir), 0.0, 1.0);
    float fres = pow(1.0 - clamp(dot(n, -rd), 0.0, 1.0), 4.0);
    float spec = pow(clamp(dot(n, h), 0.0, 1.0), 80.0);
    // Iridescent reflection — a function of view angle and surface position.
    float irid = 0.5 + 0.5 * sin(
      p.x * 4.0 + p.y * 3.0 + uTime * 0.4 + dot(n, vec3(1.0, 2.0, 3.0))
    );
    vec3 irCol = vec3(
      0.5 + 0.5 * cos(6.28 * (irid + 0.0)),
      0.5 + 0.5 * cos(6.28 * (irid + 0.33)),
      0.5 + 0.5 * cos(6.28 * (irid + 0.66))
    );
    col = vec3(0.0);
    col += diff * mix(vec3(0.05, 0.08, 0.15), vec3(0.4, 0.5, 0.65), mid);
    col += irCol * fres * (0.5 + high * 1.2);
    col += spec * vec3(1.0) * (0.6 + bass * 1.0);
  }

  float vig = pow(16.0 * vUV.x * (1.0 - vUV.x) * vUV.y * (1.0 - vUV.y), 0.25);
  col *= vig;
  col = col / (1.0 + col);
  col = pow(col, vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}`;

export function computeLiquidSkinBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.9);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.7));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.5);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.1 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.7 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.5 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 4.0 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 6.0 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 8.0 + 2.1);
}

export function computeLiquidSkinRealtimeBands(freqData: Uint8Array, out: Float32Array): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.008], [0.008, 0.030], [0.030, 0.060],
    [0.060, 0.150], [0.150, 0.260], [0.260, 0.420],
    [0.420, 0.620], [0.620, 0.800], [0.800, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    let s = 0, c = 0;
    const a = Math.floor(lo * N);
    const b = Math.floor(hi * N);
    for (let k = a; k < b; k++) { s += freqData[k]; c++; }
    out[i] = c > 0 ? (s / c) / 255 : 0;
  }
}

export function createLiquidSkinPipeline(gl: WebGL2RenderingContext): LiquidSkinPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawLiquidSkin(
  gl: WebGL2RenderingContext,
  pipe: LiquidSkinPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}